/**
 * 学习乐园 · 点灯（game-dianliang.js）
 * ============================================================
 * 「逻辑与推理」组新作：3×3 / 4×4 / 5×5 三档，默认档 3×3（入门铁律）。
 * 规则：点一格，它自己和上下左右四邻一起翻转；整面灯全点亮为目标。
 *
 * 出题底线（与数独唯一解同源）：谜面从「全亮终盘」反向按 k 个不同格生成——
 * 打乱本身就是一组合法按法，所以每一局都保证可解，绝不发出死局。
 *
 * 讲解口径（栏目底线——即时且可解释）：每按一格讲清「翻掉哪几格、还剩几盏亮」；
 * 「查一查」用模 2 高斯消元现算一组最少按法，只列格不代按——看完自己按才是练到了。
 * 点灯没有失败态（任何局面都可解），难在步数：成绩记各档最少步数与完成局数，
 * 不计时，全部存本机 localStorage。
 *
 * 冒烟钩子：window.STDianliang 暴露 neighbors / press / allOn / litCount /
 * minSolve / freeDim / scramble / puzzle 与 state()。
 */
(function (global) {
  'use strict';

  var PREF_KEY = 'study-dianliang';
  var RECORD_KEY = 'study-dianliang-record';

  /* ============ 引擎：纯函数 ============ */

  // idx 的翻转域：自身 + 上下左右在盘内的格子
  function neighbors(n, idx) {
    var out = [idx];
    var r = Math.floor(idx / n), c = idx % n;
    if (r > 0) out.push(idx - n);
    if (r < n - 1) out.push(idx + n);
    if (c > 0) out.push(idx - 1);
    if (c < n - 1) out.push(idx + 1);
    return out;
  }

  // 按一下 idx：返回新盘（纯函数）
  function press(n, board, idx) {
    var g = board.slice();
    var ns = neighbors(n, idx);
    for (var k = 0; k < ns.length; k++) g[ns[k]] = g[ns[k]] ? 0 : 1;
    return g;
  }

  function allOn(board) {
    for (var i = 0; i < board.length; i++) { if (!board[i]) return false; }
    return true;
  }

  function litCount(board) {
    var c = 0;
    for (var i = 0; i < board.length; i++) { if (board[i]) c++; }
    return c;
  }

  function shuffle(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = arr[i]; arr[i] = arr[j]; arr[j] = t;
    }
    return arr;
  }

  /* 消元主体：A[i][j]=1 表示按 j 会翻转 i，b[i]=1 XOR board[i]（暗格要被翻奇数次）。
     返回 {pivots, freeCols, A}——minSolve/freeDim 共用，行为纯函数无副作用。 */
  function elim(n, board) {
    var size = n * n;
    var A = [];
    for (var i = 0; i < size; i++) {
      var row = new Array(size + 1);
      for (var z = 0; z <= size; z++) row[z] = 0;
      var ns = neighbors(n, i);
      for (var k = 0; k < ns.length; k++) row[ns[k]] = 1;
      row[size] = board[i] ? 0 : 1;
      A.push(row);
    }
    var rowIdx = 0, pivots = [], freeCols = [];
    for (var col = 0; col < size && rowIdx < size; col++) {
      var sel = -1;
      for (var r2 = rowIdx; r2 < size; r2++) { if (A[r2][col]) { sel = r2; break; } }
      if (sel < 0) { freeCols.push(col); continue; }
      var t = A[rowIdx]; A[rowIdx] = A[sel]; A[sel] = t;
      for (var r3 = 0; r3 < size; r3++) {
        if (r3 !== rowIdx && A[r3][col]) {
          for (var c2 = col; c2 <= size; c2++) A[r3][c2] ^= A[rowIdx][c2];
        }
      }
      pivots.push(col);
      rowIdx++;
    }
    for (var r4 = rowIdx; r4 < size; r4++) {
      var allZero = true;
      for (var c3 = 0; c3 < size; c3++) { if (A[r4][c3]) { allZero = false; break; } }
      if (allZero && A[r4][size]) return null;   // 0=1 → 无解（谜面不会走到这）
    }
    return { pivots: pivots, freeCols: freeCols, A: A, size: size };
  }

  /* 最少按法：主元解 XOR 自由列组合（4×4/5×5 自由维 4，16 组枚举取最短） */
  function minSolve(n, board) {
    var e = elim(n, board);
    if (!e) return null;
    var base = new Array(e.size);
    for (var b1 = 0; b1 < e.size; b1++) base[b1] = 0;
    for (var q = 0; q < e.pivots.length; q++) base[e.pivots[q]] = e.A[q][e.size];
    if (!e.freeCols.length) {
      var out0 = [];
      for (var c0 = 0; c0 < e.size; c0++) { if (base[c0]) out0.push(c0); }
      return out0;
    }
    var best = null, bestLen = Infinity;
    var combos = 1 << e.freeCols.length;
    for (var m = 0; m < combos; m++) {
      var x = base.slice();
      for (var f = 0; f < e.freeCols.length; f++) {
        if ((m >> f) & 1) {
          x[e.freeCols[f]] = 1;
          for (var q2 = 0; q2 < e.pivots.length; q2++) x[e.pivots[q2]] ^= e.A[q2][e.freeCols[f]];
        }
      }
      var cnt = 0;
      for (var c4 = 0; c4 < e.size; c4++) { if (x[c4]) cnt++; }
      if (cnt < bestLen) { bestLen = cnt; best = x; }
    }
    var out = [];
    for (var c5 = 0; c5 < e.size; c5++) { if (best[c5]) out.push(c5); }
    return out;
  }

  // 解空间自由维数：0 = 唯一解（3×3 恒 0；4×4/5×5 经典结论恒 4）
  function freeDim(n, board) {
    var e = elim(n, board);
    return e ? e.freeCols.length : -1;
  }

  // 打乱：从全亮反向按 k 个不同格——按法集合本身就是解，可解性天然保证
  var SCRAMBLE = { 3: 4, 4: 6, 5: 8 };
  function scramble(n) {
    var size = n * n;
    var order = [];
    for (var i = 0; i < size; i++) order.push(i);
    shuffle(order);
    var flips = order.slice(0, SCRAMBLE[n] || Math.ceil(size / 2)).sort(function (a, b) { return a - b; });
    var board = [];
    for (var z = 0; z < size; z++) board.push(1);
    for (var k = 0; k < flips.length; k++) board = press(n, board, flips[k]);
    return { board: board, solution: flips };
  }

  // 一整局：打乱 + 查一查用的参考按法（现算的最少解）
  function puzzle(n) {
    var s = scramble(n);
    return { n: n, board: s.board, scrambleSolution: s.solution, refSolution: minSolve(n, s.board) };
  }

  /* ============ UI：挂载与状态 ============ */

  var LEVELS = [
    { n: 3, name: '入门 3×3', hint: '默认档 · 按法唯一' },
    { n: 4, name: '进阶 4×4', hint: '同一盘有多种按法' },
    { n: 5, name: '挑战 5×5', hint: '比谁按得省' }
  ];

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  var root = null;
  var ui = {};
  var S = { n: 3, board: [], moves: 0, ended: false, soundOn: true, resetArm: false, resetTimer: null };

  function renderLevels() {
    var box = ui.levels;
    box.textContent = '';
    for (var i = 0; i < LEVELS.length; i++) {
      (function (lv) {
        var b = el('button', 'st-llk-pack-btn');
        b.type = 'button';
        b.setAttribute('data-level', String(lv.n));
        b.setAttribute('role', 'radio');
        b.appendChild(el('span', 'st-llk-pack-name', lv.name));
        b.appendChild(el('small', 'st-llk-pack-hint', lv.hint));
        b.addEventListener('click', function () {
          if (S.n === lv.n) return;
          newGame(lv.n);   // 换档 = 直接开新局
        });
        box.appendChild(b);
      })(LEVELS[i]);
    }
  }

  function syncLevels() {
    var btns = ui.levels.querySelectorAll('[data-level]');
    for (var i = 0; i < btns.length; i++) {
      var on = Number(btns[i].getAttribute('data-level')) === S.n;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  function buildBoard() {
    var board = ui.board;
    board.textContent = '';
    board.style.setProperty('--st-dl-cols', String(S.n));
    for (var idx = 0; idx < S.board.length; idx++) {
      (function (i) {
        var b = el('button', 'st-dl-cell');
        b.type = 'button';
        b.setAttribute('data-idx', String(i));
        b.addEventListener('click', function () { onCell(i); });
        board.appendChild(b);
      })(idx);
    }
    renderCells();
  }

  function renderCells() {
    var cells = ui.board.children;
    for (var i = 0; i < cells.length; i++) {
      var node = cells[i];
      node.classList.toggle('is-on', !!S.board[i]);
      node.setAttribute('aria-label', '第 ' + (Math.floor(i / S.n) + 1) + ' 行第 ' + (i % S.n + 1) + ' 列，'
        + (S.board[i] ? '亮' : '暗'));
    }
    renderStatus();
  }

  function renderStatus() {
    if (S.ended) return;
    ui.status.className = 'st-status';
    ui.status.textContent = '入门 ' + S.n + '×' + S.n + ' · 亮着 ' + litCount(S.board)
      + '/' + S.board.length + ' 盏 · 已按 ' + S.moves + ' 下';
  }

  function renderNote(text, tone) {
    ui.note.textContent = text || '';
    ui.note.className = 'st-llk-note' + (tone ? ' is-' + tone : '');
  }

  function fmtRecKey() { return 'x' + S.n; }

  function renderRecord() {
    var box = ui.record;
    box.textContent = '';
    var rec = loadRecord();
    for (var i = 0; i < LEVELS.length; i++) {
      var lv = LEVELS[i];
      var r = rec['x' + lv.n];
      var bits = [];
      if (r && r.wins) bits.push(r.wins + ' 局');
      if (r && r.best) bits.push('最少 ' + r.best + ' 下');
      box.appendChild(recordStat(
        lv.n + '×' + lv.n + '完成',
        bits.length ? bits.join(' · ') : '还没通关过——全点亮一次就记在这里'
      ));
    }
  }

  function recordStat(label, value) {
    var d = el('div', 'st-llk-stat');
    d.appendChild(el('span', 'st-llk-stat-label', label));
    d.appendChild(el('strong', 'st-llk-stat-value', value));
    return d;
  }

  /* ---- 音效（WebAudio 本地合成，与排排序同一套音色表） ---- */

  /* 手机外放响度补偿（口径统一，由 scripts/boost-sfx-mobile.mjs 批量写入）：
     手机小喇叭对 500Hz 以下钝感、超短音感知响度低——触屏设备把提示音增益翻倍、
     低于 500Hz 的音移高八度、衰减拉长；桌面/耳机维持原响度。 */
  var SFX_MOBILE = false;
  try {
    SFX_MOBILE = (global.matchMedia && global.matchMedia('(pointer: coarse)').matches) ||
      (global.navigator && global.navigator.maxTouchPoints > 0);
  } catch (e) { /* 检测不可用按桌面处理 */ }
  var actx = null;
  function sfx(kind) {
    if (!S.soundOn) return;
    try {
      var AC = global.AudioContext || global.webkitAudioContext;
      if (!AC) return;
      if (!actx) actx = new AC();
      if (actx.state === 'suspended' && actx.resume) actx.resume();
      var t = actx.currentTime;
      var seq;
      if (kind === 'ok') seq = [[523, 0], [659, 0.07]];
      else if (kind === 'win') seq = [[523, 0], [659, 0.1], [784, 0.2], [1046, 0.3]];
      else seq = [[330, 0]];
      for (var i = 0; i < seq.length; i++) {
        var o = actx.createOscillator();
        var g = actx.createGain();
        o.type = 'triangle';
        o.frequency.setValueAtTime(SFX_MOBILE && seq[i][0] < 500 ? seq[i][0] * 2 : seq[i][0], t + seq[i][1]);
        g.gain.setValueAtTime(0.0001, t + seq[i][1]);
        g.gain.exponentialRampToValueAtTime(SFX_MOBILE ? 0.42 : 0.18, t + seq[i][1] + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, t + seq[i][1] + (SFX_MOBILE ? 0.2 : 0.14));
        o.connect(g); g.connect(actx.destination);
        o.start(t + seq[i][1]); o.stop(t + seq[i][1] + (SFX_MOBILE ? 0.26 : 0.16));
      }
    } catch (e) { /* 音频不可用静默 */ }
  }

  /* ---- 交互 ---- */

  function onCell(idx) {
    if (S.ended) return;
    var before = litCount(S.board);
    S.board = press(S.n, S.board, idx);
    S.moves++;
    sfx('ok');
    renderCells();
    if (allOn(S.board)) { winGame(); return; }
    var ns = neighbors(S.n, idx);
    renderNote('按了第 ' + (Math.floor(idx / S.n) + 1) + ' 行第 ' + (idx % S.n + 1)
      + ' 列——它和' + (ns.length - 1 ? '四邻共 ' + ns.length + ' 格' : '它自己')
      + '一起翻转，亮着的从 ' + before + ' 变成 ' + litCount(S.board) + ' 盏。', '');
  }

  /* ---- 查一查：模 2 消元现算一组最少按法，只列格不代按 ---- */

  function checkBoard() {
    if (S.ended) return;
    if (allOn(S.board)) {
      renderNote('已经全亮了——点「新开一局」再来一盘。', '');
      return;
    }
    var ref = minSolve(S.n, S.board);
    if (!ref || !ref.length) {
      renderNote('按现在的盘面算不出按法（这不该发生——每局都从可解的打乱里来）。点「新开一局」重开。', 'bad');
      return;
    }
    var names = ref.map(function (idx) {
      return '第 ' + (Math.floor(idx / S.n) + 1) + ' 行第 ' + (idx % S.n + 1) + ' 列';
    });
    renderNote('最少还要按 ' + ref.length + ' 下（已按 ' + S.moves + ' 下）。这组按法是：' + names.join('、')
      + '。道理：波及关系固定（按谁翻谁写死在规则里），所以从当前盘面到全亮的每一步都是确定推理，不是猜——自己按完才算练到了。', 'ok');
  }

  /* ---- 通关与成绩（只存本机） ---- */

  function winGame() {
    S.ended = true;
    sfx('win');
    var rec = loadRecord();
    var key = fmtRecKey();
    var cur = rec[key];
    if (!cur || typeof cur !== 'object' || Array.isArray(cur)) cur = { wins: 0, best: 0 };
    if (typeof cur.wins !== 'number') cur.wins = 0;
    if (typeof cur.best !== 'number') cur.best = 0;
    cur.wins++;
    var isRecord = !cur.best || S.moves < cur.best;
    if (isRecord) cur.best = S.moves;
    rec[key] = cur;
    try { localStorage.setItem(RECORD_KEY, JSON.stringify(rec)); } catch (e) { /* 隐私模式静默 */ }
    ui.status.className = 'st-status is-over';
    ui.status.textContent = '完成！' + S.n + '×' + S.n + ' 用了 ' + S.moves
      + ' 下全点亮' + (isRecord ? '——这一档的新纪录！' : '（你这档纪录 ' + cur.best + ' 下）');
    renderNote('点灯的思路是把「亮的分布」倒着看：想点亮角落，先按它旁边的格——波及关系固定，每一步都有理可讲。', 'ok');
    renderRecord();
    renderButtons();
  }

  function loadRecord() {
    try {
      var r = JSON.parse(localStorage.getItem(RECORD_KEY) || '{}');
      return r && typeof r === 'object' && !Array.isArray(r) ? r : {};
    } catch (e) { return {}; }
  }

  function resetRecord() {
    if (!S.resetArm) {
      S.resetArm = true;
      renderButtons();
      if (S.resetTimer) clearTimeout(S.resetTimer);
      S.resetTimer = setTimeout(function () { S.resetArm = false; S.resetTimer = null; renderButtons(); }, 3000);
      return;
    }
    S.resetArm = false;
    if (S.resetTimer) { clearTimeout(S.resetTimer); S.resetTimer = null; }
    try { localStorage.removeItem(RECORD_KEY); } catch (e) { /* 隐私模式静默 */ }
    renderRecord();
    renderButtons();
    toast('已重置点灯的本机成绩');
  }

  function renderButtons() {
    ui.newBtn.disabled = false;
    ui.resetBtn.disabled = false;
    ui.resetBtn.classList.toggle('is-arm', S.resetArm);
    ui.resetBtn.textContent = S.resetArm ? '再点一次确认重置' : '重置成绩';
  }

  function save() {
    try {
      localStorage.setItem(PREF_KEY, JSON.stringify({ v: 1, n: S.n, soundOn: S.soundOn }));
    } catch (e) { /* 存储不可用静默 */ }
  }

  function restorePrefs() {
    try {
      var d = JSON.parse(localStorage.getItem(PREF_KEY) || '{}');
      if (!d || d.v !== 1) return;
      if (d.n === 3 || d.n === 4 || d.n === 5) S.n = d.n;
      S.soundOn = d.soundOn !== false;
    } catch (e) { /* 忽略脏数据 */ }
  }

  function newGame(n) {
    var p = puzzle(n);
    S.n = n;
    S.board = p.board;
    S.moves = 0;
    S.ended = false;
    S.resetArm = false;
    if (S.resetTimer) { clearTimeout(S.resetTimer); S.resetTimer = null; }
    syncLevels();
    buildBoard();
    renderAll();
    renderRecord();
    renderNote('「入门 ' + n + '×' + n + '」：点任何一格，它和四邻一起翻转。全点亮就赢——这一局从可解的打乱里生成，肯定能点亮。', '');
    save();
  }

  function renderAll() {
    renderCells();
    renderButtons();
  }

  function bindSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      (function (b) {
        b.addEventListener('click', function () {
          S.soundOn = b.getAttribute('data-st-sound') === 'on';
          for (var j = 0; j < btns.length; j++) {
            var on = btns[j] === b;
            btns[j].classList.toggle('is-active', on);
            btns[j].setAttribute('aria-checked', on ? 'true' : 'false');
          }
          save();
        });
      })(btns[i]);
    }
    syncSoundPicker();
  }

  function syncSoundPicker() {
    var btns = root.querySelectorAll('[data-st-sound]');
    for (var i = 0; i < btns.length; i++) {
      var on = (btns[i].getAttribute('data-st-sound') === 'on') === S.soundOn;
      btns[i].classList.toggle('is-active', on);
      btns[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }
  }

  function toast(msg) {
    if (!ui.saved) return;
    ui.saved.textContent = msg;
    ui.saved.hidden = false;
    clearTimeout(toast._t);
    toast._t = setTimeout(function () { ui.saved.hidden = true; }, 2200);
  }

  /* ============ boot ============ */

  function boot() {
    root = document.querySelector('[data-st-game]');
    if (!root) return;
    ui.levels = root.querySelector('[data-st-dl-levels]');
    ui.board = root.querySelector('[data-st-dl-board]');
    ui.status = root.querySelector('[data-st-status]');
    ui.note = root.querySelector('[data-st-note]');
    ui.record = root.querySelector('[data-st-record]');
    ui.saved = root.querySelector('[data-st-saved]');
    ui.newBtn = root.querySelector('[data-st-new]');
    ui.checkBtn = root.querySelector('[data-st-check]');
    ui.resetBtn = root.querySelector('[data-st-reset]');

    restorePrefs();
    renderLevels();
    bindSoundPicker();

    ui.newBtn.addEventListener('click', function () { newGame(S.n); });
    ui.checkBtn.addEventListener('click', checkBoard);
    ui.resetBtn.addEventListener('click', resetRecord);

    newGame(S.n);
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  }

  /* ============ 冒烟与复用钩子 ============ */

  global.STDianliang = {
    neighbors: neighbors,
    press: press,
    allOn: allOn,
    litCount: litCount,
    minSolve: minSolve,
    freeDim: freeDim,
    scramble: scramble,
    puzzle: puzzle,
    state: function () { return { n: S.n, board: S.board, moves: S.moves, ended: S.ended }; }
  };
})(typeof window !== 'undefined' ? window : globalThis);
